/**
 * Evaluating the constraints a schema declares.
 *
 * Derived, never stored. A client-side rule is a pure function of the form's values, so computing
 * it on render keeps it from going stale; the reducer's `errors` is for what the *server* said,
 * which is an event and has to be remembered. `DeviceConfigForm` merges the two.
 *
 * Only what the save will carry is judged. A rule is about a value being written, not about one
 * already stored.
 *
 * Nothing here is authorisation and nothing here is the gate. backend-api validates every save and
 * `validate_device_config` guards the device write path; these exist so the form can say what is
 * wrong before asking.
 */

import { t } from '~i18n';
import { Constraint, Field, FieldValue, FieldValues, collectAllFields, Schema } from '../schema';
import { NAMED_RULES } from './namedRules';
import { isFieldEnabled } from './visibility';

const isBlank = (value: FieldValue): boolean => value === null || value === undefined || value === '';

const asNumber = (value: FieldValue): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

const COMPARE: Record<string, (a: number, b: number) => boolean> = {
  greaterThan: (a, b) => a > b,
  greaterThanOrEqual: (a, b) => a >= b,
  lessThan: (a, b) => a < b,
  lessThanOrEqual: (a, b) => a <= b,
  equalTo: (a, b) => a === b,
};

/**
 * The message for one broken constraint, or nothing while it holds.
 *
 * A blank value breaks only `notNull`. Every other rule describes the shape of a value that is
 * there, and a field left empty is the layer saying nothing about it -- which is the normal state
 * of most of a sparse overlay, not something to complain about.
 */
const breach = (constraint: Constraint, value: FieldValue, values: FieldValues): string | undefined => {
  if ('notNull' in constraint) {
    return isBlank(value) ? t(constraint.notNull.message) : undefined;
  }

  if ('range' in constraint) {
    const { min, max, message } = constraint.range;
    const numeric = asNumber(value);
    if (numeric === undefined) return undefined;
    const below = min !== undefined && numeric < min;
    const above = max !== undefined && numeric > max;
    return below || above ? t(message, { min, max }) : undefined;
  }

  if ('regex' in constraint) {
    const { regex, message } = constraint.regex;
    if (isBlank(value)) return undefined;
    try {
      return new RegExp(regex).test(String(value)) ? undefined : t(message);
    } catch {
      // An unreadable pattern is our bug, not the carer's. Refusing the save over it would strand
      // them with no way forward.
      return undefined;
    }
  }

  if ('comparator' in constraint) {
    const { type, field, message, skip_if } = constraint.comparator;
    if (skip_if && values[skip_if.field] === skip_if.equals) return undefined;
    const left = asNumber(value);
    const right = asNumber(values[field]);
    if (left === undefined || right === undefined) return undefined;
    return COMPARE[type]?.(left, right) === false ? t(message) : undefined;
  }

  const { name, message } = constraint.validation_function;
  const rule = NAMED_RULES[name];
  // A rule this dashboard has not learned. The configuration service may declare one before the
  // form knows it, and blocking the save over a rule we cannot evaluate would be worse than
  // letting backend-api -- which does enforce it -- answer.
  if (!rule) return undefined;
  return rule(value, values) ? undefined : t(message);
};

/**
 * Which value a constraint judges: its own field's, unless it names another.
 *
 * A control owning several parameters has no single value of its own, so the schema says which of
 * them the rule is about (`constraints[].validation_function.field`). The message still belongs to
 * the control, because that is what the carer can act on.
 */
const subject = (constraint: Constraint, field: Field, values: FieldValues): FieldValue => {
  const named =
    'validation_function' in constraint ? (constraint.validation_function as { field?: string }).field : undefined;
  return named ? values[named] : values[field.id];
};

/** The first broken constraint on this field, if any. One message per control: a second line about
 * the same value is noise, and the first is the one to fix. */
export const fieldBreach = (field: Field, values: FieldValues): string | undefined => {
  if (!field.constraints?.length) return undefined;
  // A control nobody can type in cannot be put right, so a rule on it would be an unsatisfiable
  // block on the whole save.
  if (!isFieldEnabled(field, values)) return undefined;

  for (const constraint of field.constraints) {
    const message = breach(constraint, subject(constraint, field, values), values);
    if (message) return message;
  }
  return undefined;
};

/**
 * Every field the save would break, keyed by field id so the control can be marked.
 *
 * `inScope` is the set of field ids this save would carry -- the changed and staged ones for a
 * form that sends a diff, every field for one that sends the whole object. A comparator still
 * reads the value of a field outside it: what it is compared against is context, not something
 * being written.
 */
export const validate = (schema: Schema, values: FieldValues, inScope: ReadonlySet<string>): Record<string, string> => {
  const errors: Record<string, string> = {};
  for (const field of collectAllFields(schema)) {
    if (!inScope.has(field.id)) continue;
    const message = fieldBreach(field, values);
    if (message) errors[field.id] = message;
  }
  return errors;
};
